import { useContext } from 'react';
import { styled } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import * as Yup from 'yup';
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import { NotificationContext } from "../../../../providers/notification";
import apiCall from "../../../../utils/apiCall";
import Qualification from './Main';

interface Qualification {
    id: number;
    QualificationId: number;
    QualificationName: string;
    Score: number;
    CreatedBy: string;
    ModifiedBy: string;
    ActiveStatus: boolean;
    IsApprovalRequired: boolean;
    ApprovalTypeId: number;
    IsDeleted: boolean;
};

interface ComponentProps {
    qualificationName: string;
    handleClose: () => void;
    id: number;
};

const UpdateQualification: React.FC<ComponentProps> = ({ qualificationName, id, handleClose }) => {
    const queryClient = useQueryClient();
    const { showNotification } = useContext(NotificationContext);

    const updateQualification = (data: Qualification) => {
        return apiCall().put(`/SetUp/UpdateQualification?id=${id}`, data);
    };

    const updateQualificationMutation = useMutation(['update-qualification'], updateQualification, {retry: 0});

    const updateQualificationHandler = async (values: FormikValues) => {
        const qualificationData: Qualification = {
            id: 0,
            QualificationId: 0,
            QualificationName: values?.name,
            Score: 0,
            CreatedBy: '',
            ModifiedBy: '',
            ActiveStatus: true,
            IsApprovalRequired: true,
            ApprovalTypeId: 0,
            IsDeleted: false,
        }

        try {
            await updateQualificationMutation.mutateAsync(qualificationData, {
                onSuccess: () => {
                    showNotification({
                        message: 'Qualification has been successfully updated',
                        type: "success",
                    });

                    queryClient.refetchQueries(['get-qualifications']);
                    handleClose();
                }
            })
        }catch(error: any) {
            showNotification({
                message: error?.response?.data?.title,
                type: 'error',
            });
        }
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().matches(/^[A-Za-z\s!@#$%^&*()/]+$/, 'Only text is allowed').required('Required'),
    });

    const formik = useFormik<FormikValues>({
        initialValues: {
            name: qualificationName ? qualificationName : '',
        },
        onSubmit: (values, ) => {
            updateQualificationHandler(values);
        },
        validationSchema,
    });

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                    <Field 
                        name='name'
                        type='text'
                        as={Input}
                        label='Name'
                        placeholder='Name'
                    />
                    {/* <Field 
                        name='status'
                        type='text'
                        as={Select}
                        label='Status'
                    /> */}
                </FormWrapper>
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton onClick={handleClose} text='Cancel'/>
                        <StyledButton 
                            type="submit" 
                            text={updateQualificationMutation.isLoading ? 'Updating' : 'Update'}
                            disabled={updateQualificationMutation.isLoading}
                        />
                    </ButtonContainer>
                </ButtonWrapper>
            </form>
        </FormikProvider>
    )
};

export default UpdateQualification;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`

